<template>
  <Page v-if="isVendor">
    <Card bg-variant="white">
      Your subscription is managed by your vendor. Please contact them for any
      changes:
      <a
        class="text-link"
        v-if="user"
        :href="`mailto:${user.vendorEmailAddress}`"
        >{{ user.vendorEmailAddress }}</a
      >
    </Card>
  </Page>
  <Page
    v-else
    no-breadcrumbs
    no-navbar
    authenticated
    data-id="upgrade-page"
    :data-loading="loading ? '1' : '0'"
    :data-has-filter="hasFilter ? '1' : '0'"
    :data-filter="filterString"
    container-class="xcontainer"
    title="Plans"
    wrapper-class="bg-white"
  >
    <template #header-actions>
      <FormBadge
        v-if="subscription"
        :variant="Variant.secondary"
        size="sm"
        :title="currentPlanLabel"
        data-id="current-subscription-badge"
        :data-current-plan-label="currentPlanLabel || '0'"
      >
        <NuxtLink class="hover:underline" to="/settings/">
          Current: {{ currentPlanLabel }}
        </NuxtLink>
      </FormBadge>
      <div
        class="flex flex-row items-center justify-center gap-3 rounded-lg bg-indigo-50 p-4"
        v-if="hasFilter"
        data-id="pricing-filter"
      >
        <Icon icon="filter" />
        <p class="font-medium">
          Filter: <span class="font-bold">{{ filterString }}</span>
        </p>

        <button
          type="button"
          data-el="upgrade-filter-clear"
          @click="resetFilter"
        >
          <Icon icon="times" />
        </button>
      </div>

      <div class="flex-1">
        <PlanHeaderButtons
          v-if="!loading"
          :change-index="changeIndex"
          :loading="loading"
        />
      </div>
    </template>
    <!-- separate loader so that currency switching is smoother than full page loader -->
    <section class="w-full" v-if="loading">
      <LoadingCard />
    </section>
    <section class="mb-10" v-else data-id="upgrade-plan-display">
      <div
        class="row align-content-center plan-displays content-center"
        :data-subscription-type="
          subscription ? subscription.subscriptionType : '0'
        "
        :data-has-subscription="subscription ? '1' : '0'"
      >
        <!-- subscription options -->
        <section v-if="planDisplays">
          <PlanDisplayer
            :pricing-params="pricingParams"
            :render-index="renderIndex"
          />
        </section>
      </div>
    </section>
  </Page>
</template>
<script setup lang="ts">
import Page from '../../components/Page.vue';
import PlanDisplayer from '../../components/modules/PlanDisplayer.vue';
import LoadingCard from '../../components/LoadingCard.vue';
import {
  loadFetchMany,
  type PricingQueryParams,
} from '../../lib/services/helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PlanHeaderButtons from '../../components/modules/PlanHeaderButtons.vue';
import { onMounted, ref, computed, watch } from 'vue';
import { useStore3 } from '../../store/main';
import { storeToRefs } from 'pinia';
import Card from '../../components/tw/Card.vue';
import FormBadge from '../../components/form/FormBadge.vue';
import { Variant } from '../../store/types';
import type { GetPlanDisplaysDesiresAddonPlanEnum } from '@mailslurp/admin-sdk';

import { useRoute, navigateTo } from '#imports';
import Icon from '../../components/Icon.vue';
import { useNuxtApp } from '#imports';
const { $captureEvent: captureEvent } = useNuxtApp();
const store3 = useStore3();
const route = useRoute();

const {
  plans: planDisplays,
  isVendor,
  user,
  subscription,
} = storeToRefs(store3);
const loading = ref(false);
const changeIndex = ref<number>(1);
const currentPlanLabel = computed(() => {
  return subscription.value?.subscriptionType ?? 'PERSONAL';
});
const renderIndex = ref(1);

/**
 * If arrived from settings page add product validation button
 */
const pricingParams = computed<PricingQueryParams | null>(() => {
  const q = route.query as PricingQueryParams;
  return Object.keys(q ?? {}).length > 0 ? q : null;
});
const filterString = computed(() => {
  if (hasFilter.value) {
    return [
      pricingParams.value?.desiresAddonPlan,
      pricingParams.value?.desiresUserPlan ? 'EXTRA_USERS' : undefined,
      pricingParams.value?.desiresPhonePlan ? 'PHONE' : undefined,
      // todo make this also support any plan limits or features
    ]
      .filter((it) => it)
      .join(', ');
  } else {
    return '';
  }
});
const hasFilter = computed(() => {
  return (
    pricingParams.value && Object.keys(pricingParams?.value ?? {}).length > 0
  );
});

function trueOrUndefined(value: boolean | undefined): boolean | undefined {
  if (value === true) {
    return true;
  } else {
    return undefined;
  }
}
const resetFilter = async () => {
  await navigateTo('/upgrade');
  store3.setFlash(null);
  renderIndex.value = renderIndex.value + 1;
};

onMounted(async () => {
  captureEvent('begin_upgrade_view', {
    has_subscription: !!store3.subscription,
  });
  // TODO enable toggle and icons when minified
  store3.setSidebarLgMinimized(true);
  await refreshPricing();
});

const refreshPricing = async () => {
  await loadFetchMany(
    [
      () =>
        store3.getAllSubscriptionData({
          userPlanQuantity: pricingParams.value?.userPlanQuantity
            ? parseInt(pricingParams.value?.userPlanQuantity)
            : undefined,
          desiresAddonPlan: pricingParams.value
            ?.desiresAddonPlan as GetPlanDisplaysDesiresAddonPlanEnum,
          desiresUserPlan: trueOrUndefined(
            pricingParams.value?.desiresUserPlan === '1',
          ),
          desiresPhonePlan: trueOrUndefined(
            pricingParams.value?.desiresPhonePlan === '1',
          ),
        }),
    ],
    store3,
    loading,
  );
};

watch(
  () => pricingParams.value,
  async () => {
    await refreshPricing();
  },
);
</script>
<style scoped>
legend {
  font-weight: 500;
}

fieldset {
  padding-bottom: 1rem;
}
</style>
